import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideImage,
  LucideX,
} from 'lucide-react'
import type { MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import type { ImagesListType } from 'react-spring-lightbox'
import Lightbox from 'react-spring-lightbox'
import { Button } from '~/components/ui'
import { cn } from '~/lib'
import type { Listing } from '~/models'

type GalleryProps = {
  className?: string
  listing: Listing
}

export default function ListingGallery({ className, listing }: GalleryProps) {
  // const isEmbed = useIsEmbed()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isGalleryFullscreen, setIsGalleryFullscreen] = useState(false)
  const galleryLabel = listing.name || listing.property.address || 'Listing'
  const images: ImagesListType = useMemo(() => {
    return (listing.images || []).map((image, index) => {
      return {
        src: image.url,
        loading: 'auto',
        alt: `${galleryLabel} photo ${index + 1}`,
      }
    })
  }, [listing.images, galleryLabel])

  const renderCloseButton = () => {
    return (
      <Button
        variant="map"
        size="map-sm"
        aria-label="Close gallery"
        className={cn(
          `absolute right-2 top-2 z-10 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75`,
          !isGalleryFullscreen && 'hidden'
        )}
        onClick={() => {
          setIsGalleryFullscreen(false)
        }}>
        <LucideX className="h-5 w-5" />
      </Button>
    )
  }

  const gotoPrevious = () =>
    currentImageIndex > 0 && setCurrentImageIndex(currentImageIndex - 1)

  const gotoNext = () =>
    currentImageIndex + 1 < images.length &&
    setCurrentImageIndex(currentImageIndex + 1)

  const updateIndex = (e: MouseEvent<any> | null, direction: number) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    let newIndex = currentImageIndex + direction
    if (newIndex < 0) newIndex = (listing.images || []).length - 1
    if (newIndex >= (listing.images || []).length) newIndex = 0
    setCurrentImageIndex(newIndex)
  }

  const renderPrevButton = () => {
    if ((listing.images || []).length >= 2) {
      return (
        <Button
          variant="map"
          size="map-sm"
          aria-label="Previous image"
          className="absolute left-2 top-1/2 z-10 -translate-y-1/2 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75 print:hidden"
          onClick={(e) => updateIndex(e, -1)}>
          <LucideChevronLeft className="h-5 w-5" />
        </Button>
      )
    }

    return null
  }

  const renderNextButton = () => {
    if ((listing.images || []).length >= 2) {
      return (
        <Button
          variant="map"
          size="map-sm"
          aria-label="Next image"
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 opacity-50 transition-opacity duration-200 hover:!opacity-100 group-hover:opacity-75 print:hidden"
          onClick={(e) => updateIndex(e, 1)}>
          <LucideChevronRight className="h-5 w-5" />
        </Button>
      )
    }

    return null
  }

  const renderCount = () => {
    return (
      <span
        className="absolute bottom-2 left-2 z-10 select-none rounded-md bg-background p-1.5 text-sm text-foreground opacity-50 shadow-xl transition-opacity duration-200 group-hover:opacity-90 print:hidden"
        aria-live="polite"
        onClick={(e) => e.stopPropagation()}>
        {currentImageIndex + 1} / {(listing.images || []).length}
      </span>
    )
  }

  const renderHeader = () => {
    return (
      <>
        {renderCloseButton()}
        {renderCount()}
      </>
    )
  }

  const renderButtons = () => {
    if ((listing.images || []).length < 2) {
      return renderCloseButton()
    }
    return (
      <>
        {renderCloseButton()}
        {renderPrevButton()}
        {renderNextButton()}
      </>
    )
  }

  return (
    <div
      className={cn(
        'aspect-video overflow-hidden bg-muted md:rounded-t-md',
        className
      )}>
      <div
        className={cn(
          `group relative aspect-video select-none md:rounded-t-md`,
          (listing.images || []).length === 0
            ? 'bg-slate-300'
            : 'bg-slate-900/75'
        )}>
        {(listing.images || []).length === 0 && (
          <LucideImage className="absolute inset-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform text-foreground/75" />
        )}
        {(listing.images || []).length > 0 && renderCount()}
        {renderButtons()}
        {(listing.images || []).map((image, index) => {
          return (
            <div
              key={index}
              className={cn(
                `absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center`,
                index !== currentImageIndex && 'hidden'
              )}>
              <button
                type="button"
                aria-label={`Open ${galleryLabel} photo ${index + 1} of ${
                  (listing.images || []).length
                }`}
                className={cn(
                  `focus-ring-primary h-full w-full cursor-pointer bg-transparent p-0`,
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                )}
                onClick={(e) => {
                  if (
                    isGalleryFullscreen &&
                    (listing.images || []).length > 1
                  ) {
                    updateIndex(e, 1)
                  } else {
                    e.stopPropagation()
                    setIsGalleryFullscreen(true)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (
                      isGalleryFullscreen &&
                      (listing.images || []).length > 1
                    ) {
                      updateIndex(null, 1)
                    } else {
                      setIsGalleryFullscreen(true)
                    }
                  }
                }}>
                <img
                  className={cn(
                    `aspect-video h-full w-full select-none break-inside-avoid object-cover transition-opacity duration-200`,
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  )}
                  src={image.url}
                  alt={`${galleryLabel} photo ${index + 1}`}
                />
              </button>
            </div>
          )
        })}
        {(listing.images || []).length > 0 && (
          <Lightbox
            className="absolute inset-0 bg-muted bg-opacity-75"
            isOpen={isGalleryFullscreen}
            onPrev={gotoPrevious}
            onNext={gotoNext}
            images={images}
            currentIndex={currentImageIndex}
            onClose={() => {
              setIsGalleryFullscreen(false)
            }}
            renderPrevButton={renderPrevButton}
            renderNextButton={renderNextButton}
            renderHeader={renderHeader}
          />
        )}
      </div>
    </div>
  )
}
